import { useMemo, useState } from "react";
import {
  Badge,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import { FiBell } from "react-icons/fi";
import {
  NOTIFICATION_TABS,
  useNotifications,
} from "Components/Notifications";
import { NotificationItem } from "Components/Notifications/NotificationItem";
import styles from "./styles.module.scss";

const startOfDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const groupNotificationsByDate = (notifications = []) => {
  const today = startOfDay(new Date());
  const yesterday = today ? new Date(today) : null;
  yesterday?.setDate(yesterday.getDate() - 1);

  const groups = {
    Today: [],
    Yesterday: [],
    Older: [],
  };

  notifications.forEach((notification) => {
    const notificationDate = startOfDay(notification.createdAt);

    if (
      notificationDate &&
      today &&
      notificationDate.getTime() === today.getTime()
    ) {
      groups.Today.push(notification);
      return;
    }

    if (
      notificationDate &&
      yesterday &&
      notificationDate.getTime() === yesterday.getTime()
    ) {
      groups.Yesterday.push(notification);
      return;
    }

    groups.Older.push(notification);
  });

  return Object.entries(groups).filter(([, items]) => items.length > 0);
};

const NotificationPopover = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    notifications,
    unreadCount,
    isLoading,
    isLoadingMore,
    isMarkingAllRead,
    hasNext,
    error,
    activeTab,
    setActiveTab,
    markAsRead,
    executeAction,
    markAllAsRead,
    loadMore,
    retry,
  } = useNotifications();

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications],
  );
  const open = Boolean(anchorEl);

  const handleListScroll = (event) => {
    if (!hasNext || isLoadingMore) return;

    const element = event.currentTarget;
    const remaining =
      element.scrollHeight - element.scrollTop - element.clientHeight;

    if (remaining < 80) {
      loadMore();
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="large"
        aria-label="Open notifications"
        aria-expanded={open}
        sx={{
          width: 36,
          height: 36,
          backgroundColor: "transparent !important",
          borderRadius: "50%",
          flex: "0 0 36px",
          color: "#737373",
          "&:hover": {
            backgroundColor: "transparent !important",
            color: "#2f2f2f",
          },
          "& svg": {
            width: 24,
            height: 24,
            strokeWidth: 1.9,
          },
          "& .MuiBadge-badge": {
            minWidth: 17,
            height: 17,
            borderRadius: "999px",
            padding: "0 4px",
            fontSize: 10,
            fontWeight: 700,
            top: 1,
            right: 1,
            border: "2px solid #fff",
          },
        }}
      >
        <Badge
          color="error"
          badgeContent={unreadCount > 0 ? unreadCount : undefined}
          overlap="circular"
        >
          <FiBell aria-hidden="true" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "calc(100vh - 88px)",
            border: "1px solid #eceff2",
            borderRadius: "16px",
            boxShadow: "0 18px 46px rgba(31, 41, 55, 0.18)",
            overflow: "hidden",
          },
        }}
      >
        <div className={styles.notificationPanel}>
          <div className={styles.notificationPanelHeader}>
            <Typography component="h2" className={styles.notificationPanelTitle}>
              Notifications
            </Typography>
            <button
              type="button"
              className={styles.markAllButton}
              onClick={markAllAsRead}
              disabled={isMarkingAllRead || unreadCount === 0}
            >
              {isMarkingAllRead ? (
                <CircularProgress size={14} />
              ) : (
                <DoneAllRoundedIcon aria-hidden="true" />
              )}
              <span>{isMarkingAllRead ? "Marking..." : "Mark all as Read"}</span>
            </button>
          </div>

          <div className={styles.notificationPanelTabs} role="tablist">
            {NOTIFICATION_TABS.map((tab) => {
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={
                    isActive
                      ? styles.notificationPanelTabActive
                      : styles.notificationPanelTab
                  }
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div
            className={styles.notificationPanelList}
            onScroll={handleListScroll}
          >
            {isLoading ? (
              <div className={styles.notificationPanelState}>
                <CircularProgress size={26} />
              </div>
            ) : error ? (
              <div className={styles.notificationPanelState}>
                <Typography>Failed to load notifications.</Typography>
                <button type="button" onClick={retry}>
                  Try again
                </button>
              </div>
            ) : groupedNotifications.length === 0 ? (
              <div className={styles.notificationPanelState}>
                <Typography>
                  {activeTab === "unread"
                    ? "No unread notifications"
                    : activeTab === "request"
                      ? "No requests"
                      : "No notifications yet"}
                </Typography>
              </div>
            ) : (
              groupedNotifications.map(([label, items]) => (
                <section key={label} className={styles.notificationGroup}>
                  <Typography
                    component="h3"
                    className={styles.notificationGroupTitle}
                  >
                    {label}
                  </Typography>
                  {items.map((item) => (
                    <NotificationItem
                      key={item.id}
                      notification={item}
                      onAction={executeAction}
                      onRead={markAsRead}
                      compact
                    />
                  ))}
                </section>
              ))
            )}

            {isLoadingMore && (
              <div className={styles.notificationLoadingMore}>
                <CircularProgress size={18} />
              </div>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default NotificationPopover;
